import { apiRequest, ApiError, isTransientStatus } from '../api/client'
import {
  claimOutbox,
  deleteClaimedOutbox,
  invalidateCache,
  listOutbox,
  putCached,
  updateClaimedOutbox,
  updateOutbox,
  type OutboxRecord,
} from './db'
import { dependencyKey, notifyQueueChanged } from './outbox'

export interface SyncProgress {
  processed: number
  succeeded: number
  attention: number
  deferred: number
  /** Действия, отклонённые сервером насовсем в этом прогоне (работа уже завершена и т. п.). */
  obsolete: number
}

/** После стольких неудачных попыток отправка продолжается, но действие показывается как требующее внимания. */
export const STUCK_ATTEMPTS = 8

export interface SyncSession {
  readonly sessionId: string
  readonly userId: string
  readonly token: string
}

export const AUTH_EXPIRED_EVENT = 'amalthea:auth-expired'

interface RunningSync {
  controller: AbortController
  session: SyncSession
}

const tabId = crypto.randomUUID()
let runSequence = 0
let activeSession: SyncSession | null = null
const runningBySession = new Map<string, Promise<SyncProgress>>()
const controllersBySession = new Map<string, RunningSync>()

const emptyProgress = (): SyncProgress => ({ processed: 0, succeeded: 0, attention: 0, deferred: 0, obsolete: 0 })

/**
 * Что делать с действием, которое сервер отверг окончательно (4xx, кроме 401 и временных ошибок).
 * Полевые действия по заданию или дефекту (чек-лист, фото, взятие, старт, завершение) устаревают: сервер уже в другом
 * состоянии — работа завершена или отменена другим способом, и ждать решения исполнителя бессмысленно. Такое действие
 * не блокирует остальные и переходит в список «Отклонено сервером», откуда его можно повторить или удалить.
 * Версионный конфликт оборудования и отказы по планированию по-прежнему ждут явного решения.
 */
export function rejectionVerdict(record: OutboxRecord, error: ApiError): 'conflict' | 'obsolete' | 'failed' {
  if (error.status === 412 || error.code === 'EQUIPMENT_VERSION_CONFLICT') return 'conflict'
  if (error.status === 404 || error.status === 410) return 'obsolete'
  const key = dependencyKey(record)
  if (key.startsWith('work-order:') || key.startsWith('defects:')) return 'obsolete'
  return error.status === 409 ? 'conflict' : 'failed'
}

/**
 * Записи, помеченные прежней версией приложения как «конфликт» или «отказ» по окончательному ответу сервера
 * (например, 409 «работа уже завершена»), по новым правилам устарели: они не должны ждать решения и держать хвост.
 */
function isLegacyObsolete(record: OutboxRecord): boolean {
  const status = record.responseStatus
  if (typeof status !== 'number' || status < 400 || status >= 500 || status === 401 || isTransientStatus(status)) return false
  return rejectionVerdict(record, new ApiError(status, { code: record.errorCode })) === 'obsolete'
}
const sessionKey = (session: SyncSession) => `${session.userId}:${session.sessionId}`
const sameSession = (left: SyncSession | null, right: SyncSession | null) => {
  if (!left || !right) return left === right
  return left.sessionId === right.sessionId && left.userId === right.userId && left.token === right.token
}

/**
 * Switches the session allowed to drain this tab's outbox and synchronously
 * aborts requests captured by every older session.
 */
export function activateSyncSession(session: SyncSession | null): void {
  const snapshot = session ? Object.freeze({ ...session }) : null
  if (sameSession(activeSession, snapshot)) return
  activeSession = snapshot

  for (const [key, running] of controllersBySession) {
    if (!sameSession(running.session, snapshot)) {
      running.controller.abort('Authentication session changed')
      controllersBySession.delete(key)
      runningBySession.delete(key)
    }
  }
}

export function isSyncSessionActive(session: SyncSession): boolean {
  return sameSession(activeSession, session)
}

function formFromRecord(record: OutboxRecord): FormData {
  const form = new FormData()
  for (const [name, value] of Object.entries(record.fields ?? {})) form.append(name, value)
  if (record.fileBlob) form.append('file', record.fileBlob, record.fileName || 'upload.bin')
  return form
}

async function executeRecord(record: OutboxRecord, session: SyncSession, signal: AbortSignal): Promise<unknown> {
  const body = record.bodyType === 'multipart' ? formFromRecord(record) : record.jsonBody
  return apiRequest(record.path, {
    method: record.method,
    headers: record.headers,
    body,
    idempotencyKey: record.idempotencyKey,
    authToken: session.token,
    notifyOnUnauthorized: false,
    signal,
  })
}

function retryDelay(attempts: number): number {
  return Math.min(15 * 60_000, 2 ** Math.min(attempts, 9) * 2_000)
}

async function releaseCancelledClaim(record: OutboxRecord, leaseOwner: string): Promise<void> {
  await updateClaimedOutbox(record.id, leaseOwner, {
    state: 'queued',
    attempts: Math.max(0, record.attempts - 1),
    nextAttemptAt: Date.now(),
    lastError: undefined,
    responseStatus: undefined,
  })
}

async function run(session: SyncSession, signal: AbortSignal, leaseOwner: string): Promise<SyncProgress> {
  const progress = emptyProgress()
  if (!navigator.onLine || !isSyncSessionActive(session) || signal.aborted) return progress

  const records = await listOutbox(session.userId)
  // Порядок важен внутри одной зависимости (одно задание, один дефект, одна единица оборудования):
  // застрявшее действие задерживает только свой хвост, остальные задания продолжают отправляться.
  const blocked = new Set<string>()
  for (const candidate of records) {
    if (!isSyncSessionActive(session) || signal.aborted) break
    if (candidate.state === 'auth-required') {
      progress.attention += 1
      window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT, { detail: { userId: session.userId } }))
      break
    }
    const key = dependencyKey(candidate)
    if (blocked.has(key)) {
      progress.deferred += 1
      continue
    }
    if (candidate.state === 'obsolete') continue
    if ((candidate.state === 'conflict' || candidate.state === 'failed') && isLegacyObsolete(candidate)) {
      const migrated = await updateOutbox(candidate.id, { state: 'obsolete' })
      if (migrated) {
        progress.obsolete += 1
        notifyQueueChanged()
        continue
      }
    }
    if (candidate.state === 'conflict' || candidate.state === 'failed') {
      progress.attention += 1
      blocked.add(key)
      continue
    }
    if (candidate.nextAttemptAt > Date.now()) {
      progress.deferred += 1
      blocked.add(key)
      continue
    }

    const record = await claimOutbox(candidate.id, session.userId, leaseOwner)
    if (!record) {
      // Another tab either owns this record or changed it after our snapshot.
      // Its dependent actions wait so they are not sent ahead of it.
      progress.deferred += 1
      blocked.add(key)
      continue
    }

    if (!isSyncSessionActive(session) || signal.aborted) {
      await releaseCancelledClaim(record, leaseOwner)
      progress.deferred += 1
      notifyQueueChanged()
      break
    }

    progress.processed += 1
    notifyQueueChanged()

    try {
      const response = await executeRecord(record, session, signal)
      if (!isSyncSessionActive(session) || signal.aborted) {
        await releaseCancelledClaim(record, leaseOwner)
        progress.deferred += 1
        break
      }

      const completed = await deleteClaimedOutbox(record.id, leaseOwner)
      if (!completed) {
        progress.deferred += 1
        blocked.add(key)
        continue
      }

      progress.succeeded += 1
      await invalidateCache(session.userId).catch(() => undefined)
      if (
        isSyncSessionActive(session)
        && record.path === '/plans/generate'
        && response
        && typeof response === 'object'
        && 'id' in response
      ) {
        const plan = response as { id: string }
        await putCached(session.userId, `plan:${plan.id}`, response)
        if (isSyncSessionActive(session)) await putCached(session.userId, 'plans:last', response)
      }
      if (isSyncSessionActive(session)) window.dispatchEvent(new CustomEvent('amalthea:data-changed'))
    } catch (error) {
      if (!isSyncSessionActive(session) || signal.aborted) {
        await releaseCancelledClaim(record, leaseOwner)
        progress.deferred += 1
        break
      }

      if (error instanceof ApiError && error.status === 401) {
        const updated = await updateClaimedOutbox(record.id, leaseOwner, {
          state: 'auth-required',
          responseStatus: error.status,
          lastError: 'Войдите снова под той же учётной записью, чтобы отправить действие.',
        })
        if (!updated) {
          progress.deferred += 1
          break
        }
        progress.attention += 1
        window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT, { detail: { userId: session.userId } }))
        break
      }
      if (error instanceof ApiError && error.status >= 400 && error.status < 500 && !isTransientStatus(error.status)) {
        const verdict = rejectionVerdict(record, error)
        const updated = await updateClaimedOutbox(record.id, leaseOwner, {
          state: verdict,
          responseStatus: error.status,
          errorCode: error.code,
          lastError: error.message,
        })
        if (!updated) {
          progress.deferred += 1
          blocked.add(key)
          continue
        }
        if (verdict === 'obsolete') {
          // Хвост того же задания не ждёт: каждое следующее действие сервер оценит само.
          progress.obsolete += 1
          continue
        }
        progress.attention += 1
        blocked.add(key)
        continue
      }

      // Временная ошибка (сеть, таймаут, 5xx): отправка повторяется без ограничения числа попыток с растущей паузой
      // до 15 минут, чтобы плохая связь не превращала очередь в «застрявшую навсегда».
      const stuck = record.attempts >= STUCK_ATTEMPTS
      const updated = await updateClaimedOutbox(record.id, leaseOwner, {
        state: 'retry',
        responseStatus: error instanceof ApiError ? error.status : undefined,
        errorCode: error instanceof ApiError ? error.code : undefined,
        lastError: error instanceof Error ? error.message : 'Не удалось отправить действие',
        nextAttemptAt: Date.now() + retryDelay(record.attempts),
      })
      if (!updated) {
        progress.deferred += 1
        blocked.add(key)
        continue
      }
      if (stuck) progress.attention += 1
      else progress.deferred += 1
      // A deferred head still blocks the dependent actions behind it (same key); other keys go on.
      blocked.add(key)
      continue
    } finally {
      notifyQueueChanged()
    }
  }

  return progress
}

export function syncOutbox(session: SyncSession): Promise<SyncProgress> {
  const snapshot = Object.freeze({ ...session })
  if (!isSyncSessionActive(snapshot)) return Promise.resolve(emptyProgress())

  const key = sessionKey(snapshot)
  const existing = runningBySession.get(key)
  if (existing) return existing

  const controller = new AbortController()
  const leaseOwner = `${tabId}:${snapshot.sessionId}:${++runSequence}`
  controllersBySession.set(key, { controller, session: snapshot })

  const promise = run(snapshot, controller.signal, leaseOwner).finally(() => {
    if (runningBySession.get(key) === promise) runningBySession.delete(key)
    if (controllersBySession.get(key)?.controller === controller) controllersBySession.delete(key)
  })
  runningBySession.set(key, promise)
  return promise
}
